import React, { createContext, FC, ReactNode, useEffect, useState } from "react";
import axios from "axios";

interface UserContextType {
    userId: string | null;
    isLoggedIn: boolean;
    login: (id: string, token: string) => void;
    logout: () => void;
    setIsLoggedIn: (value: boolean) => void;
}

export const UserContext = createContext<UserContextType>({
    userId: null,
    isLoggedIn: false,
    login: () => {
    },
    logout: () => {
    },
    setIsLoggedIn: () => {
    },
});

export const UserProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [ userId, setUserId ] = useState<string | null>(null);
    const [ isLoggedIn, setIsLoggedIn ] = useState<boolean>(!!localStorage.getItem("token"));

    useEffect(() => {
        // Read userId and token from localStorage on the first mount
        const storedUserId = localStorage.getItem("userId");
        const token = localStorage.getItem("token");

        if (storedUserId && token) {
            setUserId(storedUserId);
            setIsLoggedIn(true);

            // Set the token in Axios headers
            axios.defaults.headers["Authorization"] = `Bearer ${token}`;
        }
    }, []);

    const login = (id: string, token: string) => {
        setUserId(id);
        setIsLoggedIn(true);
        localStorage.setItem("userId", id);
        localStorage.setItem("token", token);

        // Set the token in Axios headers
        axios.defaults.headers["Authorization"] = `Bearer ${token}`;
    };

    const logout = () => {
        setUserId(null);
        setIsLoggedIn(false);
        localStorage.removeItem("userId");
        localStorage.removeItem("token");

        // Remove the token from Axios headers
        delete axios.defaults.headers["Authorization"];
    };

    // Intercept Axios responses to handle 401 Unauthorized globally
    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    setIsLoggedIn(false);
                    logout(); // Clear user state and localStorage on unauthorized access
                }
                return Promise.reject(error);
            },
        );

        return () => {
            axios.interceptors.response.eject(interceptor); // Cleanup the interceptor on unmount
        };
    }, []);

    return (
        <UserContext.Provider value={{ userId, isLoggedIn, login, logout, setIsLoggedIn }}>
            {children}
        </UserContext.Provider>
    );
};
